import type {
  DetailedCircuitFeature,
  DetailedCircuitGeometry,
  TrackContributionGpsCoord,
  TrackContributionPayload,
} from '@kangaroo/shared';

import {
  parseTrackCatalogWkt,
  type TrackCatalogSegmentLine,
  type TrackCatalogTrackConfig,
  type TrackCatalogV2TrackConfig,
  type WktCoordinate,
} from './trackCatalog';
import { withDerivedGeometricApexes } from './detailedCircuitGeometry';

function toGpsCoord(point: WktCoordinate): TrackContributionGpsCoord {
  return { lat: point.lat, lon: point.lng };
}

function parsePaths(wkt?: string | null) {
  return parseTrackCatalogWkt(wkt).map((path) => path.map(toGpsCoord));
}

function midpoint(points: TrackContributionGpsCoord[]) {
  if (points.length === 0) {
    return null;
  }
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lon: points.reduce((sum, point) => sum + point.lon, 0) / points.length,
  };
}

function polygonCenter(rings: TrackContributionGpsCoord[][]) {
  return midpoint(rings[0] ?? []);
}

function distanceM(left: TrackContributionGpsCoord, right: TrackContributionGpsCoord) {
  const earthRadiusM = 6_371_000;
  const lat1 = left.lat * Math.PI / 180;
  const lat2 = right.lat * Math.PI / 180;
  const deltaLat = (right.lat - left.lat) * Math.PI / 180;
  const deltaLon = (right.lon - left.lon) * Math.PI / 180;
  const a =
    Math.sin(deltaLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  return earthRadiusM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function samePoint(left: TrackContributionGpsCoord, right: TrackContributionGpsCoord) {
  return distanceM(left, right) < 0.15;
}

function appendUnique(points: TrackContributionGpsCoord[], point: TrackContributionGpsCoord | null) {
  if (!point || (points.length > 0 && samePoint(points.at(-1)!, point))) {
    return;
  }
  points.push(point);
}

function lineMidpoint(wkt?: string | null) {
  return midpoint(parsePaths(wkt)[0] ?? []);
}

function isLapSection(segment: TrackCatalogSegmentLine) {
  const type = segment.segmentType.toLowerCase();
  return type === 'corner' || type === 'straight';
}

function segmentNumber(segment: TrackCatalogSegmentLine) {
  return Number(segment.label.match(/\d+/)?.[0] ?? Number.MAX_SAFE_INTEGER);
}

function orderedLapSections(segments: TrackCatalogSegmentLine[]) {
  const sections = segments.filter(isLapSection);
  const children = new Map<string, TrackCatalogSegmentLine[]>();
  for (const section of sections) {
    if (!section.parentUuid) {
      continue;
    }
    const group = children.get(section.parentUuid) ?? [];
    group.push(section);
    children.set(section.parentUuid, group);
  }

  const starts = sections
    .filter((section) => !section.parentUuid)
    .sort((left, right) => {
      const straightRank = (segment: TrackCatalogSegmentLine) =>
        segment.segmentType.toLowerCase() === 'straight' ? 0 : 1;
      return straightRank(left) - straightRank(right) || segmentNumber(left) - segmentNumber(right);
    });
  const ordered: TrackCatalogSegmentLine[] = [];
  const visited = new Set<string>();
  let current = starts[0];
  while (current && !visited.has(current.uuid)) {
    ordered.push(current);
    visited.add(current.uuid);
    current = (children.get(current.uuid) ?? [])
      .filter((candidate) => !visited.has(candidate.uuid))
      .sort((left, right) => segmentNumber(left) - segmentNumber(right))[0];
  }

  if (ordered.length < sections.length) {
    ordered.push(
      ...sections
        .filter((section) => !visited.has(section.uuid))
        .sort((left, right) => segmentNumber(left) - segmentNumber(right)),
    );
  }
  return ordered;
}

function normalizedClosedRing(ring: TrackContributionGpsCoord[]) {
  if (ring.length > 1 && samePoint(ring[0]!, ring.at(-1)!)) {
    return ring.slice(0, -1);
  }
  return ring;
}

function closedRingLengthM(ring: TrackContributionGpsCoord[]) {
  let length = 0;
  for (let index = 0; index < ring.length; index += 1) {
    length += distanceM(ring[index]!, ring[(index + 1) % ring.length]!);
  }
  return length;
}

function resampleClosedRing(ring: TrackContributionGpsCoord[], count: number) {
  const normalized = normalizedClosedRing(ring);
  if (normalized.length < 3) {
    return [];
  }
  const segmentLengths = normalized.map((point, index) =>
    distanceM(point, normalized[(index + 1) % normalized.length]!));
  const totalLength = segmentLengths.reduce((sum, length) => sum + length, 0);
  if (totalLength <= 0) {
    return [];
  }

  const result: TrackContributionGpsCoord[] = [];
  let segmentIndex = 0;
  let segmentStartDistance = 0;
  for (let sampleIndex = 0; sampleIndex < count; sampleIndex += 1) {
    const targetDistance = totalLength * sampleIndex / count;
    while (
      segmentIndex < segmentLengths.length - 1
      && segmentStartDistance + segmentLengths[segmentIndex]! < targetDistance
    ) {
      segmentStartDistance += segmentLengths[segmentIndex]!;
      segmentIndex += 1;
    }
    const start = normalized[segmentIndex]!;
    const end = normalized[(segmentIndex + 1) % normalized.length]!;
    const segmentLength = segmentLengths[segmentIndex] || 1;
    const fraction = Math.max(
      0,
      Math.min(1, (targetDistance - segmentStartDistance) / segmentLength),
    );
    result.push({
      lat: start.lat + (end.lat - start.lat) * fraction,
      lon: start.lon + (end.lon - start.lon) * fraction,
    });
  }
  return result;
}

function alignmentDistanceSquared(
  left: TrackContributionGpsCoord,
  right: TrackContributionGpsCoord,
) {
  const metersPerDegreeLat = 111_320;
  const metersPerDegreeLon =
    metersPerDegreeLat * Math.cos((left.lat + right.lat) * Math.PI / 360);
  return (
    ((left.lon - right.lon) * metersPerDegreeLon) ** 2
    + ((left.lat - right.lat) * metersPerDegreeLat) ** 2
  );
}

function orientCenterline(
  centerline: TrackContributionGpsCoord[],
  guide: TrackContributionGpsCoord[],
) {
  if (centerline.length < 3 || guide.length < 2) {
    return centerline;
  }
  let startIndex = 0;
  let closestDistance = Number.POSITIVE_INFINITY;
  centerline.forEach((point, index) => {
    const distance = alignmentDistanceSquared(point, guide[0]!);
    if (distance < closestDistance) {
      closestDistance = distance;
      startIndex = index;
    }
  });

  const lookAhead = Math.max(1, Math.round(centerline.length / 32));
  const forward = centerline[(startIndex + lookAhead) % centerline.length]!;
  const backward = centerline[
    (startIndex - lookAhead + centerline.length) % centerline.length
  ]!;
  const guideVector = {
    lat: guide[1]!.lat - guide[0]!.lat,
    lon: guide[1]!.lon - guide[0]!.lon,
  };
  const forwardDot =
    (forward.lat - centerline[startIndex]!.lat) * guideVector.lat
    + (forward.lon - centerline[startIndex]!.lon) * guideVector.lon;
  const backwardDot =
    (backward.lat - centerline[startIndex]!.lat) * guideVector.lat
    + (backward.lon - centerline[startIndex]!.lon) * guideVector.lon;
  const direction = forwardDot >= backwardDot ? 1 : -1;

  return centerline.map((_, index) =>
    centerline[
      (startIndex + direction * index + centerline.length * 2) % centerline.length
    ]!);
}

function ribbonCenterline(
  outlineRings: TrackContributionGpsCoord[][],
  guide: TrackContributionGpsCoord[],
) {
  const rings = outlineRings
    .map(normalizedClosedRing)
    .filter((ring) => ring.length >= 3)
    .sort((left, right) => closedRingLengthM(right) - closedRingLengthM(left));
  if (rings.length < 2) {
    return [];
  }

  const perimeterM = (
    closedRingLengthM(rings[0]!)
    + closedRingLengthM(rings[1]!)
  ) / 2;
  const sampleCount = Math.max(96, Math.min(512, Math.round(perimeterM / 8)));
  const outer = resampleClosedRing(rings[0]!, sampleCount);
  const inner = resampleClosedRing(rings[1]!, sampleCount);
  if (outer.length !== sampleCount || inner.length !== sampleCount) {
    return [];
  }

  const scoreStep = Math.max(1, Math.floor(sampleCount / 32));
  let bestShift = 0;
  let bestDirection = 1;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const direction of [1, -1]) {
    for (let shift = 0; shift < sampleCount; shift += 1) {
      let score = 0;
      for (let index = 0; index < sampleCount; index += scoreStep) {
        const innerIndex =
          (shift + direction * index + sampleCount * 2) % sampleCount;
        score += alignmentDistanceSquared(outer[index]!, inner[innerIndex]!);
      }
      if (score < bestScore) {
        bestScore = score;
        bestShift = shift;
        bestDirection = direction;
      }
    }
  }

  const centerline = outer.map((point, index) => {
    const matchingInner = inner[
      (bestShift + bestDirection * index + sampleCount * 2) % sampleCount
    ]!;
    return {
      lat: (point.lat + matchingInner.lat) / 2,
      lon: (point.lon + matchingInner.lon) / 2,
    };
  });
  return orientCenterline(centerline, guide);
}

function fallbackOutlinePolyline(config: TrackCatalogTrackConfig) {
  const outerRing = parsePaths(config.polygonAsText)[0] ?? [];
  if (outerRing.length > 1 && samePoint(outerRing[0]!, outerRing.at(-1)!)) {
    return outerRing.slice(0, -1);
  }
  return outerRing;
}

export function buildCatalogLapPolyline(config: TrackCatalogTrackConfig) {
  const guide: TrackContributionGpsCoord[] = [];
  for (const section of orderedLapSections(config.segmentLines)) {
    appendUnique(guide, lineMidpoint(section.entryLineAsText));
    appendUnique(guide, lineMidpoint(section.exitLineAsText));
  }
  const explicitCenterline = parsePaths(config.centreLineAsText)[0] ?? [];
  const derivedCenterline = ribbonCenterline(parsePaths(config.polygonAsText), guide);
  const polyline =
    explicitCenterline.length >= 2
      ? orientCenterline(normalizedClosedRing(explicitCenterline), guide)
      : derivedCenterline.length >= 2
        ? derivedCenterline
        : guide.length >= 2
          ? guide
          : fallbackOutlinePolyline(config);
  if (polyline.length > 2 && !samePoint(polyline[0]!, polyline.at(-1)!)) {
    polyline.push({ ...polyline[0]! });
  }
  return polyline;
}

function nearestFraction(polyline: TrackContributionGpsCoord[], target: TrackContributionGpsCoord) {
  if (polyline.length < 2) {
    return 0;
  }
  const cumulative = [0];
  for (let index = 1; index < polyline.length; index += 1) {
    cumulative.push(cumulative[index - 1]! + distanceM(polyline[index - 1]!, polyline[index]!));
  }
  const total = cumulative.at(-1) || 1;
  const metersPerDegreeLat = 111_320;
  const metersPerDegreeLon = metersPerDegreeLat * Math.cos(target.lat * Math.PI / 180);
  let bestDistanceSquared = Number.POSITIVE_INFINITY;
  let bestDistanceAlong = 0;

  for (let index = 1; index < polyline.length; index += 1) {
    const start = polyline[index - 1]!;
    const end = polyline[index]!;
    const startX = (start.lon - target.lon) * metersPerDegreeLon;
    const startY = (start.lat - target.lat) * metersPerDegreeLat;
    const deltaX = (end.lon - start.lon) * metersPerDegreeLon;
    const deltaY = (end.lat - start.lat) * metersPerDegreeLat;
    const lengthSquared = deltaX ** 2 + deltaY ** 2;
    const projection = lengthSquared > 0
      ? Math.max(0, Math.min(1, -(startX * deltaX + startY * deltaY) / lengthSquared))
      : 0;
    const projectedX = startX + projection * deltaX;
    const projectedY = startY + projection * deltaY;
    const distanceSquared = projectedX ** 2 + projectedY ** 2;
    if (distanceSquared < bestDistanceSquared) {
      bestDistanceSquared = distanceSquared;
      bestDistanceAlong =
        cumulative[index - 1]!
        + projection * (cumulative[index]! - cumulative[index - 1]!);
    }
  }
  return Math.max(0, Math.min(1, bestDistanceAlong / total));
}

function lapLengthM(polyline: TrackContributionGpsCoord[]) {
  let length = 0;
  for (let index = 1; index < polyline.length; index += 1) {
    length += distanceM(polyline[index - 1]!, polyline[index]!);
  }
  return Math.max(length, 1);
}

function lapDirection(polyline: TrackContributionGpsCoord[]) {
  let signedArea = 0;
  for (let index = 0; index < polyline.length; index += 1) {
    const current = polyline[index]!;
    const next = polyline[(index + 1) % polyline.length]!;
    signedArea += current.lon * next.lat - next.lon * current.lat;
  }
  return signedArea >= 0 ? 'counterclockwise' as const : 'clockwise' as const;
}

function detailedFeature(
  segment: TrackCatalogSegmentLine,
  v2ById: Map<string, TrackCatalogV2TrackConfig['segments'][number]>,
): DetailedCircuitFeature {
  const v2 = v2ById.get(segment.uuid);
  return {
    id: segment.uuid,
    parentId: segment.parentUuid,
    type: segment.segmentType,
    label: segment.label,
    polygonRings: parsePaths(segment.polygonAsText ?? v2?.polygonAsText),
    entryLine: parsePaths(segment.entryLineAsText)[0] ?? [],
    exitLine: parsePaths(segment.exitLineAsText)[0] ?? [],
    directionalPoint: parsePaths(segment.entryDirectionalPointAsText)[0]?.[0] ?? null,
    bounds: parsePaths(v2?.boundingBoxAsText),
  };
}

export function buildDetailedCircuitGeometry(
  config: TrackCatalogTrackConfig,
  v2?: TrackCatalogV2TrackConfig | null,
): DetailedCircuitGeometry {
  const v2ById = new Map((v2?.segments ?? []).map((segment) => [segment.trackConfigSegmentUuid, segment]));
  const centerLine = buildCatalogLapPolyline(config);
  return withDerivedGeometricApexes({
    version: 1,
    outlineRings: parsePaths(config.polygonAsText),
    ...(centerLine?.length ? { centerLine } : {}),
    features: config.segmentLines.map((segment) => detailedFeature(segment, v2ById)),
  });
}

export function buildTrackCatalogContributionPayload(
  trackId: string,
  config: TrackCatalogTrackConfig,
  v2?: TrackCatalogV2TrackConfig | null,
): TrackContributionPayload {
  const gpsPolyline = buildCatalogLapPolyline(config);
  if (gpsPolyline.length < 2) {
    throw new Error('This layout does not contain enough geometry to save as a circuit.');
  }

  const startFinish = config.segmentLines.find(
    (segment) => segment.segmentType.toLowerCase() === 'startfinish',
  );
  const startLine = parsePaths(startFinish?.entryLineAsText)[0] ?? [];
  const pointA = startLine[0] ?? gpsPolyline[0]!;
  const pointB = startLine[1] ?? gpsPolyline[1]!;
  const detailedGeometry = buildDetailedCircuitGeometry(config, v2);
  const detailedFeatureById = new Map(
    detailedGeometry.features.map((feature) => [feature.id, feature]),
  );
  const corners = config.segmentLines.filter(
    (segment) => segment.segmentType.toLowerCase() === 'corner',
  );
  const cornerApexSuggestions = corners.flatMap((segment, index) => {
    const point =
      detailedFeatureById.get(segment.uuid)?.apexPoint
      ?? polygonCenter(parsePaths(segment.polygonAsText));
    if (!point) {
      return [];
    }
    return [{
      id: segment.uuid,
      label: segment.label,
      lat: point.lat,
      lon: point.lon,
      turnNumber: Number(segment.label.match(/\d+/)?.[0] ?? index + 1),
      confidence: 1,
    }];
  });
  const segmentSuggestions = config.segmentLines
    .filter((segment) => segment.segmentType.toLowerCase() === 'sector')
    .flatMap((segment) => {
      const entry = lineMidpoint(segment.entryLineAsText);
      const exit = lineMidpoint(segment.exitLineAsText);
      if (!entry || !exit) {
        return [];
      }
      return [{
        id: segment.uuid,
        label: segment.label,
        startFraction: nearestFraction(gpsPolyline, entry),
        endFraction: nearestFraction(gpsPolyline, exit),
        confidence: 1,
      }];
    });
  const startMidpoint = midpoint([pointA, pointB])!;

  return {
    trackId,
    gpsPolyline,
    startLineSuggestion: { pointA, pointB, confidence: startFinish ? 1 : 0.25 },
    segmentSuggestions,
    cornerApexSuggestions,
    direction: lapDirection(gpsPolyline),
    lapLengthEstimateM: lapLengthM(gpsPolyline),
    confidenceScore: 0.9,
    startFraction: nearestFraction(gpsPolyline, startMidpoint),
    detailedGeometry,
  };
}

export function trackCatalogLocation(wkt: string) {
  const point = parsePaths(wkt)[0]?.[0];
  if (!point) {
    throw new Error('This track does not contain a valid location.');
  }
  return point;
}

export function trackCatalogContributionSessionKey(configUuid: string) {
  return `track-catalog:${configUuid}`;
}
